import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { FloatingChatProvider } from './contexts/FloatingChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/ScrollToTop';
import KeepAlive from './components/KeepAlive';
import InstallPrompt from './components/InstallPrompt';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import FloatingChatRoot from './components/FloatingChatRoot';
import { CookieBanner } from './components/CookieBanner';
import { PremiumAdFree } from './components/PremiumAdFree';

// Pages
import Landing from './pages/Landing';
import { Login, Register } from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import CompleteProfile from './pages/CompleteProfile';
import Dashboard from './pages/Dashboard';
import Club from './pages/Club';
import FindClub from './pages/FindClub';
import Matches from './pages/Matches';
import Championship from './pages/Championship';
import Chat from './pages/Chat';
import MatchChats from './pages/MatchChats';
import Friends from './pages/Friends';
import Admin from './pages/Admin';
import Shop from './pages/Shop';
import Contracts from './pages/Contracts';
import SuperAdmin from './pages/SuperAdmin';
import Stats from './pages/Stats';
import Profile from './pages/Profile';
import Moderation from './pages/Moderation';
import Mediazioni from './pages/admin/Mediazioni';
import Leaderboard from './pages/Leaderboard';
import Tornei from './pages/Tornei';
import Calciomercato from './pages/Calciomercato';
import AlboDoro from './pages/AlboDoro';
import Regolamento from './pages/Regolamento';
import Privacy from './pages/Privacy';
import UserProfile from './pages/UserProfile';

function AppRouter() {
  const location = useLocation();

  // Persist the last-visited route so a cold reload from app-switch on
  // mobile (Chrome killed the tab in background) lands back where the
  // user was instead of the landing/dashboard. The Login/AuthCallback
  // pages restore it on successful auth.
  React.useEffect(() => {
    const path = location.pathname + location.search;
    const skip = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/auth/callback'];
    if (!skip.includes(location.pathname)) {
      try { sessionStorage.setItem('lastRoute', path); } catch { /* noop */ }
      try { localStorage.setItem('lastRoute', path); } catch { /* noop */ }
    }
  }, [location.pathname, location.search]);

  // Check URL fragment for session_id (OAuth callback) on ANY page
  // This handles when Google redirects back with #session_id=xxx
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/classifica" element={<Leaderboard />} />
      <Route path="/tornei" element={<Tornei />} />
      <Route path="/calciomercato" element={<Calciomercato />} />
      <Route path="/albo-d-oro" element={<AlboDoro />} />
      <Route path="/regolamento" element={<Regolamento />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route 
        path="/complete-profile" 
        element={
          <ProtectedRoute allowIncompleteProfile={true}>
            <CompleteProfile />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/club" element={<Club />} />
        <Route path="/find-club" element={<FindClub />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/tornei-partite" element={<Matches mode="tournament" />} />
        <Route path="/championship" element={<Championship />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/:userId" element={<UserProfile />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/match-chats" element={<MatchChats />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/stats" element={<Stats />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole={['admin', 'super_admin']}>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <SuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/moderation"
          element={
            <ProtectedRoute requiredRole={['moderator', 'admin', 'super_admin']}>
              <Moderation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/mediazioni"
          element={
            <ProtectedRoute requiredRole={['admin', 'super_admin']}>
              <Mediazioni />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <FloatingChatProvider>
            <KeepAlive />
            <InstallPrompt />
            <PushNotificationPrompt />
            <AppRouter />
            <CookieBanner />
            <PremiumAdFree />
            <FloatingChatRoot />
            <ScrollToTop />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: '#151921',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                },
              }}
            />
          </FloatingChatProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
